import { Modal, Form, Row, Col, Typography, Divider, Space, Alert } from 'antd';
import type { FC } from 'react';
import { useState, useCallback } from 'react';
import { DriveFolderType, type SerializedMeterModel } from '@enmon/commons/lib/tools';
import { ManufacturersSelect } from '@/components/form/select/ManufacturersSelect';
import { MeterModelsSelect } from '@/components/form/select/MeterModelsSelect';
import { ModelFilesGallery } from '@/components/enmon/files/model_library_files_gallery';
import { useUpdateMeter } from '@/api/hooks';
import { useUpdateInventoryMeter } from '@/api/hooks/inventory-meter/useUpdateInventoryMeter';
import type { PopulatedCombinedMeter } from '@/lib/services';
import { useUserInfo } from '@/hooks/useUser';
import { queryClient } from '@/api/services/queryClient';
import { QUERY_KEYS } from '@/utils/queryKeys';
import { Button } from '@/components';
import { useMetersByModelIdWithPagination } from '@/routes/reseller-data/routes/model-library/routes/models/routes/detail/route/meters/hooks/useMetersFilterWithPagination';
import type { Paths } from 'type-fest';
import { useParams } from 'react-router-dom';
import { useMeterModels } from '@/api/hooks/meter-models/useMeterModels';
import { MeterModelFilesCardType } from '@/routes/reseller-data/routes/model-library/routes/models/routes/detail/constants';

const { Title, Text } = Typography;

interface ReviewMeterModalProps {
  isOpen: boolean;
  meter: PopulatedCombinedMeter | null;
  onClose: () => void;
}

interface ReviewMeterModalFormValues {
  manufacturerId?: number;
  meterModelId?: number;
}

export const ReviewMeterModal: FC<ReviewMeterModalProps> = ({ isOpen, meter, onClose }) => {
  const { id } = useParams<{ id: string }>();
  const currentModelId = Number(id); // Model ID from the detail page route

  const meterCurrentManufacturer = meter?.meterModel?.manufacturer;
  const meterCurrentModel = meter?.meterModel;
  const user = useUserInfo();

  const [form] = Form.useForm<ReviewMeterModalFormValues>();

  const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | undefined>();
  const [selectedModel, setSelectedModel] = useState<SerializedMeterModel | undefined>();
  const [isUpdatingMode, setIsUpdatingMode] = useState(false);

  const { filter } = useMetersByModelIdWithPagination();

  const { data: meterModelData, isFetching: isLoadingMeterModel } = useMeterModels(
    { ids: [currentModelId] },
    { enabled: !!currentModelId },
  );

  const [currentModel] = meterModelData?.data ?? [];

  const { mutate: updateMeter, isLoading: isUpdatingMeter } = useUpdateMeter();
  const { mutate: updateInventoryMeter, isLoading: isUpdatingInventoryMeter } = useUpdateInventoryMeter();

  const handleManufacturerChange = (value: number) => {
    setSelectedManufacturerId(value);
    // Reset meter model when manufacturer changes
    form.setFieldValue('meterModelId', undefined);
    setSelectedModel(undefined);
  };

  const handleModelChange = (model: SerializedMeterModel | null) => {
    setSelectedModel(model ?? undefined);
  };

  // A model picked from another family member takes its own manufacturer with it
  const handleModelManufacturerChange = (manufacturerId: number) => {
    form.setFieldValue('manufacturerId', manufacturerId);
    setSelectedManufacturerId(manufacturerId);
  };

  const resetModalState = useCallback(() => {
    form.resetFields();
    setSelectedManufacturerId(undefined);
    setSelectedModel(undefined);
    setIsUpdatingMode(false);
    onClose();
  }, [form, onClose]);

  const markAsReviewed = useCallback(() => {
    if (!meter) return;

    updateInventoryMeter(
      {
        id: Number(meter.toolsId),
        data: {
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.email,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(QUERY_KEYS.meterModels.meters(currentModel.id, filter));
          resetModalState();
        },
      },
    );
  }, [meter, currentModel, filter, updateInventoryMeter, user.email, resetModalState]);

  const handleConfirm = () => {
    // Just mark as reviewed without any changes
    markAsReviewed();
  };

  const handleUpdate = (values: ReviewMeterModalFormValues) => {
    if (!meter) return;

    updateMeter(
      {
        meterId: meter._id,
        values: {
          manufacturer: values.manufacturerId,
          meterModel: values.meterModelId,
        },
      },
      {
        onSuccess: () => markAsReviewed(),
      },
    );
  };

  const handleCancel = () => {
    resetModalState();
  };

  const handleCancelUpdate = () => {
    form.resetFields();
    setSelectedManufacturerId(undefined);
    setSelectedModel(undefined);
    setIsUpdatingMode(false);
  };

  const isLoading = isLoadingMeterModel || isUpdatingMeter || isUpdatingInventoryMeter;
  const assignedManufacturerName = meterCurrentManufacturer?.name ?? 'Not assigned';
  const assignedModelName = meterCurrentModel?.name ?? 'Not assigned';

  if (!meter) return null;

  return (
    <Modal
      open={isOpen}
      title={`Review Meter: ${meter.name}`}
      width={1000}
      onCancel={handleCancel}
      footer={
        <Space>
          <Button onClick={handleCancel}>Cancel</Button>
          {!isUpdatingMode ? (
            <>
              <Button type="primary" onClick={handleConfirm} loading={isLoading}>
                Confirm - Assignment is Correct
              </Button>
              <Button type="default" onClick={() => setIsUpdatingMode(true)}>
                Update Assignment
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCancelUpdate}>Back</Button>
              <Button type="primary" onClick={() => form.submit()} loading={isLoading}>
                Save Changes
              </Button>
            </>
          )}
        </Space>
      }
    >
      {/* Meter's Current Assignment */}
      <Divider size="large">
        <Title level={5} style={{ margin: 0 }}>
          Meter Current Assignment
        </Title>
      </Divider>
      <div style={{ padding: '16px', background: '#fafafa', borderRadius: 4, marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Manufacturer: </Text>
            <Text>{assignedManufacturerName}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Model: </Text>
            <Text>{assignedModelName}</Text>
          </Col>
        </Row>
      </div>

      {/* Expected Model Photos (from model detail page context) */}
      <Divider size="large">
        <Title level={5}>Expected Model Photos</Title>
      </Divider>
      <ModelFilesGallery
        model={currentModel}
        folderType={DriveFolderType.Photos}
        visible={isOpen && !!currentModel?.id}
        emptyMessage="No model photos available"
        source={MeterModelFilesCardType.Model}
        enablePreview
      />

      {/* Meter Photos */}
      <Divider size="large">
        <Title level={5}>Meter Photos</Title>
      </Divider>
      <ModelFilesGallery
        meter={meter}
        folderType={DriveFolderType.Photos}
        visible={isOpen && !!meter._id}
        emptyMessage="No meter photos available"
        source={MeterModelFilesCardType.Meter}
        enablePreview
      />

      {!isUpdatingMode && (
        <Alert
          title="Is this assignment correct?"
          description="Click 'Confirm' if the assignment is correct, or 'Update Assignment' if it needs to be changed."
          type="info"
          showIcon
          style={{ margin: '16px 0' }}
        />
      )}

      {/* Edit Section - New Assignment (only shown when updating) */}
      {isUpdatingMode && (
        <>
          <Divider size="large">
            <Title level={5}>Update Assignment</Title>
          </Divider>

          <Form form={form} layout="vertical" onFinish={handleUpdate}>
            <Form.Item
              label="New Manufacturer"
              name={'manufacturerId' satisfies Paths<ReviewMeterModalFormValues>}
              rules={[{ required: true, message: 'Please select a manufacturer' }]}
            >
              <ManufacturersSelect commodity={meter.commodity} onChange={handleManufacturerChange} />
            </Form.Item>
            <Form.Item
              label="New Meter Model"
              name={'meterModelId' satisfies Paths<ReviewMeterModalFormValues>}
              rules={[{ required: true, message: 'Please select a meter model' }]}
            >
              <MeterModelsSelect
                filter={{ manufacturerId: selectedManufacturerId, commodity: meter.commodity }}
                disabled={!selectedManufacturerId}
                placeholder={selectedManufacturerId ? 'Select meter model' : 'Select manufacturer first'}
                onModelSelect={handleModelChange}
                onModelManufacturerChange={handleModelManufacturerChange}
              />
            </Form.Item>

            {selectedModel && selectedManufacturerId && (
              <>
                <Divider size="large">
                  <Title level={5}>New Model Photos</Title>
                </Divider>
                <ModelFilesGallery
                  model={selectedModel}
                  folderType={DriveFolderType.Photos}
                  visible={isOpen && !!selectedModel.id}
                  emptyMessage="Select a meter model to view photos"
                  source={MeterModelFilesCardType.Model}
                  enablePreview
                />
              </>
            )}
          </Form>
        </>
      )}
    </Modal>
  );
};

export const useReviewMeterModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  return {
    isOpen,
    closeModal,
    openModal,
    Modal: ReviewMeterModal,
  };
};
